import React, { useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faRightFromBracket } from "@fortawesome/free-solid-svg-icons";
import SportsMotorsportsIcon from "@mui/icons-material/SportsMotorsports";
import SettingsSuggestIcon from "@mui/icons-material/SettingsSuggest";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";

import theme from "../../styles/theme";
import { useUserContext } from "../../contexts/user";

const Container = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  position: relative;
  height: 60px;
  background-color: ${({ $open }) => ($open ? "rgb(48 48 49)" : "transparent")};
  transition: background-color 160ms ease;

  &:hover {
    background-color: rgb(48 48 49);
  }
`;

const Trigger = styled.button`
  all: unset;
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  color: ${theme.colors.white};
  font-family: "Montserrat", sans-serif;
  font-size: 14px;
  font-weight: 600;
  height: 100%;
  padding: 0 10px;
`;

const Name = styled.span``;

const Chevron = styled(FontAwesomeIcon)`
  font-size: 12px;
  color: ${theme.colors.grey};
  transition: transform 160ms ease;
  transform: rotate(${({ $open }) => ($open ? "180deg" : "0deg")});
`;

const AvatarWrapper = styled.div`
  width: 38px;
  height: 38px;
  border-radius: 50%;
  overflow: hidden;
  border: 2px solid ${theme.colors.grey};
  background: ${theme.colors.dark};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
`;

const AvatarImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const Dropdown = styled.div`
  position: absolute;
  top: 60px;
  right: 0;
  background: #1f1f1f;
  border: 2px solid #3c3c3e;
  border-radius: 4px;
  min-width: 200px;
  box-shadow: 1px 2px 4px rgba(0, 0, 0, 0.35);
  padding: 8px;
  z-index: 200;
`;

const MenuItem = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 10px 12px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  background: transparent;
  color: ${theme.colors.white};
  font-family: "Montserrat", sans-serif;
  font-size: 15px;
  transition: background 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.06);
  }
`;

const Divider = styled.div`
  height: 1px;
  background: ${theme.colors.grey};
  margin: 6px 0;
  opacity: 0.4;
`;

const IconSlot = styled.span`
  width: 22px;
  min-width: 22px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-right: 8px;
`;

const UserMenu = () => {
  const { user, logout } = useUserContext();
  const [open, setOpen] = useState(false);
  const menuRef = useRef(null);
  const navigate = useNavigate();

  const displayName = useMemo(() => {
    if (user?.member) {
      const first = user.member?.firstName || "";
      const last = user.member?.lastName || "";
      const name = `${first} ${last}`.trim();
      if (name) return name;
    }

    return user.discordUser?.globalName;
  }, [user]);

  const hasDriver = Boolean(user?.driver?.id);
  const driverId = user?.driver?.id || null;

  const avatarSrc = user?.discordUser?.avatar;

  const toggle = () => setOpen((prev) => !prev);

  const close = () => setOpen(false);

  const handleProfile = () => {
    if (driverId) {
      navigate(`/drivers/${driverId}`);
    }
    close();
  };

  const handleSettings = () => {
    navigate(`/settings`);
    close();
  };

  const handleLogout = async () => {
    await logout();
    close();
    navigate("/");
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <Container ref={menuRef} $open={open}>
      <Trigger onClick={toggle} title="Account menu" aria-haspopup="menu" $open={open}>
        <Chevron icon={faChevronDown} $open={open} />

        <AvatarWrapper>
          {avatarSrc ? (
            <AvatarImg src={avatarSrc} alt={displayName} />
          ) : (
            <Name>{displayName?.charAt(0)?.toUpperCase() || "?"}</Name>
          )}
        </AvatarWrapper>
      </Trigger>
      {open && (
        <Dropdown>
          {hasDriver && (
            <MenuItem onClick={handleProfile}>
              <IconSlot>
                <SportsMotorsportsIcon sx={{ fontSize: 22 }} />
              </IconSlot>
              <Name>{displayName}</Name>
            </MenuItem>
          )}
          <Divider />
          <MenuItem onClick={handleSettings}>
            <IconSlot>
              <AccountBalanceWalletIcon sx={{ fontSize: 22 }} />
            </IconSlot>
            <Name>Інвентар</Name>
          </MenuItem>
          <Divider />
          <MenuItem onClick={handleLogout}>
            <IconSlot>
              <FontAwesomeIcon icon={faRightFromBracket} fixedWidth style={{ fontSize: 22 }} />
            </IconSlot>
            Вийти
          </MenuItem>
        </Dropdown>
      )}
    </Container>
  );
};

export default UserMenu;
